import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RightWrapper from '../CommonComponents/ComponentWrapper';
import { DataTable, Button, Modal, ConfirmDialog } from '../UI';
import { FiTrash2, FiEye, FiCheck, FiRotateCcw, FiCheckCircle } from 'react-icons/fi';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import fetchAllPages, { formatExportDateTime } from '../../UtilityHookFunctions/fetchAllPages';

/**
 * Universal DynamicFormViewer
 * Consolidates the inbound form pages in Routes/FormData with Ticket Status & Actions
 */
export default function DynamicFormViewer({
  title,
  subtitle = 'Inbound user form submissions from the public website',
  headers = [],
  fetchAction,
  deleteAction,
  updateAction,
  stateSliceName, // e.g. 'bookAlternativeTherapies'
  dataKey, // e.g. 'getallBookAlternativeTherapies'
}) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'open' | 'closed'
  const [viewingRow, setViewingRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const sliceState = useSelector((state) => state?.[stateSliceName] || {});
  const rawData = sliceState?.[dataKey] || sliceState?.data || [];
  const totalPages = sliceState?.totalPages || 1;
  const isLoading = Boolean(sliceState?.loading);

  useEffect(() => {
    if (fetchAction) {
      dispatch(fetchAction({ page, limit, search: searchTerm }));
    }
  }, [dispatch, fetchAction, page, limit, searchTerm]);

  // Client-side status filter fallback if endpoint returns mixed
  const filteredData = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    if (statusFilter === 'all') return rawData;
    if (statusFilter === 'closed') {
      return rawData.filter((r) => r.status === 'closed');
    }
    // 'open' matches open or undefined/missing status
    return rawData.filter((r) => r.status !== 'closed');
  }, [rawData, statusFilter]);

  const counts = useMemo(() => {
    if (!Array.isArray(rawData)) return { all: 0, open: 0, closed: 0 };
    const closed = rawData.filter((r) => r.status === 'closed').length;
    return {
      all: rawData.length,
      closed,
      open: rawData.length - closed,
    };
  }, [rawData]);

  const handleDeleteConfirm = async () => {
    if (!deletingId || !deleteAction) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteAction(deletingId));
      toast.success('Record deleted successfully');
      setDeletingId(null);
      if (fetchAction) {
        dispatch(fetchAction({ page, limit, search: searchTerm }));
      }
    } catch (error) {
      toast.error('Failed to delete record');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (row) => {
    if (!updateAction) {
      toast.info('Status update is not enabled for this form');
      return;
    }
    const targetId = row._id || row.id;
    const isClosed = row.status === 'closed';
    const newStatus = isClosed ? 'open' : 'closed';

    setUpdatingId(targetId);
    try {
      await dispatch(updateAction({ id: targetId, _id: targetId, status: newStatus })).unwrap?.() ||
        await dispatch(updateAction({ id: targetId, _id: targetId, status: newStatus }));

      toast.success(newStatus === 'closed' ? 'Ticket marked as Closed' : 'Ticket reopened as Open');
      if (fetchAction) {
        dispatch(fetchAction({ page, limit, search: searchTerm }));
      }
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  // Exports every submission matching the current search + status filter (all pages).
  const handleExport = async () => {
    if (!fetchAction) return;
    setIsExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, pageSize) => {
        const action = await dispatch(fetchAction({ page: pageNo, limit: pageSize, search: searchTerm }));
        return action?.payload;
      });
      const rows =
        statusFilter === 'all'
          ? all
          : all.filter((r) => (statusFilter === 'closed' ? r.status === 'closed' : r.status !== 'closed'));
      if (!rows.length) {
        toast.info('No records to export');
        return;
      }
      const columns = [
        ...headers.map((h) => {
          const key = typeof h === 'string' ? h : h.key;
          const label = typeof h === 'string' ? h : h.label || h.key;
          return {
            key,
            label,
            format: (value) => {
              if (/date|createdAt|updatedAt/i.test(String(key))) return formatExportDateTime(value);
              if (Array.isArray(value)) return value.join(', ');
              if (value && typeof value === 'object') return JSON.stringify(value);
              return value ?? '';
            },
          };
        }),
        { key: 'status', label: 'Status', format: (value) => (value === 'closed' ? 'Closed' : 'Open') },
        { key: 'createdAt', label: 'Submitted On', format: formatExportDateTime },
      ];
      exportToExcel(rows, title || 'form_submissions', columns);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      // Restore the page the user was looking at (the export loop reused the same slice).
      dispatch(fetchAction({ page, limit, search: searchTerm }));
      setIsExporting(false);
    }
  };

  // Build table column definitions with Status and Action buttons
  const tableHeaders = [
    ...headers.map((h) => (typeof h === 'string' ? { key: h, label: h } : h)),
    {
      label: 'Status',
      key: 'status',
      render: (statusVal, row) => {
        const isClosed = row.status === 'closed';
        const isBusy = updatingId === (row._id || row.id);
        return (
          <button
            type="button"
            onClick={() => handleToggleStatus(row)}
            disabled={isBusy}
            title={isClosed ? 'Ticket is Closed. Click to Reopen' : 'Ticket is Open. Click to Close'}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition border shadow-xs ${
              isClosed
                ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400'
            } ${isBusy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
          >
            {isClosed ? (
              <>
                <FiCheckCircle className="w-3.5 h-3.5 text-slate-500" />
                <span>Closed</span>
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>Open</span>
              </>
            )}
          </button>
        );
      },
    },
    {
      label: 'Actions',
      key: '_id',
      render: (id, row) => {
        const isClosed = row.status === 'closed';
        const isBusy = updatingId === (row._id || row.id);
        return (
          <div className="flex items-center gap-1.5">
            {updateAction && (
              <Button
                size="xs"
                variant={isClosed ? 'ghost' : 'outline'}
                onClick={() => handleToggleStatus(row)}
                disabled={isBusy}
                title={isClosed ? 'Reopen Ticket' : 'Mark Ticket as Closed'}
                className={
                  isClosed
                    ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold'
                }
              >
                {isClosed ? (
                  <span className="flex items-center gap-1">
                    <FiRotateCcw className="w-3.5 h-3.5" /> Reopen
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <FiCheck className="w-3.5 h-3.5 text-emerald-600" /> Close
                  </span>
                )}
              </Button>
            )}
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setViewingRow(row)}
              title="View Details"
            >
              <FiEye className="w-3.5 h-3.5 text-sky-600" />
            </Button>
            {deleteAction && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setDeletingId(id || row?.id)}
                title="Delete Record"
                className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
              >
                <FiTrash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <RightWrapper>
      <div className="p-4 md:p-6 w-full space-y-4">
        {/* Status Filter Tabs */}
        <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider pl-1">
              Filter:
            </span>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-xl text-xs font-semibold transition ${
                statusFilter === 'all'
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              All ({counts.all})
            </button>
            <button
              onClick={() => setStatusFilter('open')}
              className={`px-3 py-1 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 ${
                statusFilter === 'open'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              Open ({counts.open})
            </button>
            <button
              onClick={() => setStatusFilter('closed')}
              className={`px-3 py-1 rounded-xl text-xs font-semibold transition ${
                statusFilter === 'closed'
                  ? 'bg-slate-700 text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Closed ({counts.closed})
            </button>
          </div>

          <div className="text-xs text-slate-400 font-medium hidden sm:block">
            Click status badge or Close button to toggle ticket state
          </div>
        </div>

        <DataTable
          title={title}
          subtitle={subtitle}
          headers={tableHeaders}
          data={filteredData}
          toolbar={
            <Button variant="outline" size="sm" onClick={handleExport} isLoading={isExporting}>
              Export to Excel
            </Button>
          }
          page={page}
          totalPages={totalPages}
          rowsPerPage={limit}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          onSearch={(term) => {
            setSearchTerm(term);
            setPage(1);
          }}
          isLoading={isLoading}
          emptyMessage={`No ${title} submissions found.`}
        />

        {/* View Details Modal */}
        <Modal
          isOpen={Boolean(viewingRow)}
          onClose={() => setViewingRow(null)}
          title={`${title} Submission Details`}
          subtitle="Full submitted inquiry payload"
          maxWidth="max-w-xl"
          footer={
            <div className="flex items-center justify-between w-full">
              {updateAction && viewingRow && (
                <Button
                  size="sm"
                  variant={viewingRow.status === 'closed' ? 'outline' : 'primary'}
                  onClick={() => {
                    handleToggleStatus(viewingRow);
                    setViewingRow((prev) =>
                      prev ? { ...prev, status: prev.status === 'closed' ? 'open' : 'closed' } : null
                    );
                  }}
                  className={
                    viewingRow.status === 'closed'
                      ? ''
                      : 'bg-emerald-600 hover:bg-emerald-700 border-emerald-600'
                  }
                >
                  {viewingRow.status === 'closed' ? (
                    <span className="flex items-center gap-1.5">
                      <FiRotateCcw className="w-3.5 h-3.5" /> Reopen Ticket
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <FiCheck className="w-3.5 h-3.5" /> Mark as Closed
                    </span>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewingRow(null)}
              >
                Close
              </Button>
            </div>
          }
        >
          {viewingRow && (
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="font-semibold text-slate-500 uppercase tracking-wider text-[11px]">
                  Ticket Status
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    viewingRow.status === 'closed'
                      ? 'bg-slate-200 text-slate-700'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}
                >
                  {viewingRow.status === 'closed' ? 'Closed' : 'Open'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(viewingRow)
                  .filter(([k]) => !['_id', '__v'].includes(k))
                  .map(([key, val]) => (
                    <div key={key} className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                      <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] block">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </span>
                      <span className="text-slate-800 text-sm mt-0.5 block break-words">
                        {typeof val === 'object' ? JSON.stringify(val) : String(val || '—')}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={Boolean(deletingId)}
          onClose={() => setDeletingId(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Submission Record"
          message="Are you sure you want to delete this form submission? This action is permanent and cannot be undone."
          confirmText="Delete Submission"
          isLoading={isDeleting}
        />

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </RightWrapper>
  );
}
